import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import Axios from '../Axios';
import consts from '../constant';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

const DEPOSIT_HISTORY_PAGE_SIZE = 10;
const HISTORY_STATUS_FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Approved', value: 'approved' },
  { label: 'Pending', value: 'pending' },
  { label: 'Reject', value: 'reject' }
];

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function formatDate(value) {
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function getStatusClassName(status) {
  const normalizedStatus = String(status || '').toLowerCase();
  if (['completed', 'approved', 'success'].includes(normalizedStatus)) {
    return 'is-completed';
  }
  if (normalizedStatus === 'pending') {
    return 'is-pending';
  }
  return 'is-failed';
}

function normalizeDepositStatus(status) {
  const normalized = String(status || '').toLowerCase();
  if (['completed', 'approved', 'approve', 'success'].includes(normalized)) {
    return 'Approved';
  }
  if (['failed', 'rejected', 'reject', 'cancelled', 'canceled'].includes(normalized)) {
    return 'Reject';
  }
  return 'Pending';
}

function normalizeDepositHistoryItem(item = {}, index = 0) {
  const fallbackCreatedAt = item?.createdAt || item?.updatedAt || new Date().toISOString();
  return {
    id: item?._id || item?.id || `DEP-${String(fallbackCreatedAt)}-${index}`,
    createdAt: fallbackCreatedAt,
    amount: Number(item?.amount ?? item?.deposit_amount ?? item?.value ?? 0),
    approvedAmount: item?.approved_amount ?? item?.approvedAmount ?? '',
    reference: item?.reference || item?.image || item?.proof || '-',
    status: normalizeDepositStatus(item?.status)
  };
}

function normalizeWithdrawHistoryItem(item = {}, index = 0) {
  const fallbackCreatedAt = item?.createdAt || item?.updatedAt || new Date().toISOString();
  return {
    id: item?._id || item?.id || `WDR-${String(fallbackCreatedAt)}-${index}`,
    createdAt: fallbackCreatedAt,
    address: item?.address || item?.wallet_address || item?.walletAddress || item?.to_address || '-',
    network: item?.network || item?.network_type || item?.networkType || item?.chain || '-',
    amount: Number(item?.amount ?? item?.withdraw_amount ?? item?.value ?? 0),
    status: normalizeDepositStatus(item?.status),
    reason: item?.reason || item?.remarks || item?.reject_reason || '-'
  };
}

function resolveReferenceImageUrl(reference) {
  const value = String(reference || '').trim();
  if (!value || value === '-') {
    return '';
  }
  if (value.startsWith('blob:') || value.startsWith('data:image/') || /^https?:\/\//i.test(value)) {
    return value;
  }
  const normalizedBase = String(consts?.BackendUrl || '').replace(/\/+$/, '');
  if (!normalizedBase) {
    return value;
  }
  if (value.startsWith('/')) {
    return `${normalizedBase}${value}`;
  }
  return `${normalizedBase}/${value.replace(/^\/+/, '')}`;
}

function buildPaginationItems(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);

  if (currentPage <= 3) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
  }

  if (currentPage >= totalPages - 2) {
    pages.add(totalPages - 1);
    pages.add(totalPages - 2);
    pages.add(totalPages - 3);
  }

  const sortedPages = [...pages]
    .filter((pageNumber) => pageNumber >= 1 && pageNumber <= totalPages)
    .sort((first, second) => first - second);

  const items = [];
  for (let index = 0; index < sortedPages.length; index += 1) {
    const current = sortedPages[index];
    const previous = sortedPages[index - 1];
    if (index > 0 && current - previous > 1) {
      items.push('ellipsis');
    }
    items.push(current);
  }

  return items;
}

export function WalletTransactionPage({ mode = 'deposit' }) {
  const isDepositMode = mode === 'deposit';
  const [wallet, setWallet] = useState({ balance: 0 });
  const [amount, setAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawNetwork, setWithdrawNetwork] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const imageInputRef = useRef(null);
  const imagePreviewUrl = useMemo(() => (imageFile ? URL.createObjectURL(imageFile) : ''), [imageFile]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(Number(totalRecords || 0) / DEPOSIT_HISTORY_PAGE_SIZE)),
    [totalRecords]
  );
  const paginationItems = useMemo(
    () => buildPaginationItems(page, totalPages),
    [page, totalPages]
  );

  const getWallet = useCallback(async () => {
    try {
      const { data } = await Axios.post('/users/getWallet', {});
      if (data?.success) {
        setWallet(data?.result || { balance: 0 });
      } else {
        setWallet({ balance: 0 });
      }
    } catch (error) {
      setWallet({ balance: 0 });
      console.error('Error fetching wallet:', error);
    }
  }, []);

  useEffect(() => {
    getWallet();
  }, [getWallet]);

  useEffect(() => {
    setPage(1);
    setStatusFilter('all');
    setTransactions([]);
    setTotalRecords(0);
  }, [isDepositMode]);

  useEffect(() => () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
  }, [imagePreviewUrl]);

  const getWalletHistory = useCallback(async () => {
    setIsHistoryLoading(true);
    try {
      const statusCandidates = statusFilter === 'all'
        ? ['all', '']
        : statusFilter === 'approved'
          ? ['approved', 'approve']
          : statusFilter === 'reject'
            ? ['reject', 'rejected']
            : [statusFilter];

      if (isDepositMode) {
        let data = null;
        for (let index = 0; index < statusCandidates.length; index += 1) {
          const candidateStatus = statusCandidates[index];
          const payload = candidateStatus ? { status: candidateStatus } : {};
          const response = await Axios.post(`/users/get-deposit-history?page=${page}&limit=${DEPOSIT_HISTORY_PAGE_SIZE}`, {
            ...payload
          });
          data = response?.data || null;

          const hasRows = Array.isArray(data?.result) && data.result.length > 0;
          const hasTotal = Number(data?.total || 0) > 0;
          const isLastCandidate = index === statusCandidates.length - 1;
          if (hasRows || hasTotal || isLastCandidate) {
            break;
          }
        }

        if (Array.isArray(data?.result)) {
          setTransactions(data.result.map((item, index) => normalizeDepositHistoryItem(item, index)));
          setTotalRecords(Number(data?.total || 0));
        } else {
          setTransactions([]);
          setTotalRecords(0);
        }
      } else {
        let data = null;
        for (let index = 0; index < statusCandidates.length; index += 1) {
          const candidateStatus = statusCandidates[index];
          const payload = candidateStatus ? { status: candidateStatus } : {};
          const response = await Axios.post(`/users/get-withdraw-history?page=${page}&limit=${DEPOSIT_HISTORY_PAGE_SIZE}`, {
            ...payload
          });
          data = response?.data || null;

          const hasRows = Array.isArray(data?.result) && data.result.length > 0;
          const hasTotal = Number(data?.total || 0) > 0;
          const isLastCandidate = index === statusCandidates.length - 1;
          if (hasRows || hasTotal || isLastCandidate) {
            break;
          }
        }

        if (Array.isArray(data?.result)) {
          setTransactions(data.result.map((item, index) => normalizeWithdrawHistoryItem(item, index)));
          setTotalRecords(Number(data?.total || 0));
        } else {
          setTransactions([]);
          setTotalRecords(0);
        }
      }
    } catch (error) {
      setTransactions([]);
      setTotalRecords(0);
      console.error('Error fetching wallet history:', error);
    } finally {
      setIsHistoryLoading(false);
    }
  }, [isDepositMode, page, statusFilter]);

  useEffect(() => {
    getWalletHistory();
  }, [getWalletHistory]);

  const resetForm = useCallback(() => {
    setAmount('');
    setWithdrawAddress('');
    setWithdrawNetwork('');
    setImageFile(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  }, []);

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setImageFile(null);
      return;
    }
    if (!file.type || !file.type.startsWith('image/')) {
      toast.error('Please upload a valid image file.');
      event.target.value = '';
      setImageFile(null);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB.');
      event.target.value = '';
      setImageFile(null);
      return;
    }
    setImageFile(file);
  };

  const uploadDepositImage = async () => {
    if (!imageFile) {
      toast.error('Please upload a proof image.');
      return null;
    }
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      const { data } = await Axios.post('/uploadimage', formData);
      if (data?.success && data?.result) {
        return data.result;
      }
      toast.error(data?.message || 'Unable to upload image.');
      return null;
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Unable to upload image.');
      return null;
    }
  };

  const handleDepositSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }

    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      toast.error('Enter a valid amount.');
      return;
    }
    if (!imageFile) {
      toast.error('Please upload a proof image.');
      return;
    }

    setIsSubmitting(true);
    try {
      const reference = await uploadDepositImage();
      if (!reference) {
        return;
      }

      const payload = { amount: parsedAmount, reference };
      const { data } = await Axios.post('/users/fiat-deposit', payload);

      if (!data?.success) {
        toast.error(data?.message || 'Failed to create deposit request.');
        return;
      }

      toast.success(data?.message || 'Deposit request created successfully.');
      resetForm();
      await getWallet();
      if (page !== 1) {
        setPage(1);
      } else {
        await getWalletHistory();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to create deposit request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWithdrawSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }

    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      toast.error('Enter a valid withdrawal amount.');
      return;
    }
    const trimmedAddress = withdrawAddress.trim();
    if (!trimmedAddress) {
      toast.error('Withdraw address is required.');
      return;
    }
    if (trimmedAddress.length < 6) {
      toast.error('Enter a valid withdraw address.');
      return;
    }
    const trimmedNetwork = withdrawNetwork.trim();
    if (!trimmedNetwork) {
      toast.error('Network is required.');
      return;
    }

    const walletBalance = Number(wallet?.balance || 0);
    if (!Number.isFinite(walletBalance)) {
      toast.error('Wallet balance is unavailable. Please refresh.');
      return;
    }
    if (parsedAmount > walletBalance) {
      toast.error(`Insufficient balance. Available: ${formatCurrency(walletBalance)}`);
      return;
    }

    setIsSubmitting(true);
    try {
      const { data } = await Axios.post('/users/fiat-withdraw', {
        amount: parsedAmount,
        address: trimmedAddress,
        network: trimmedNetwork
      });

      if (!data?.success) {
        toast.error(data?.message || 'Failed to create withdraw request.');
        return;
      }

      toast.success(data?.message || 'Withdraw request submitted successfully.');
      setAmount('');
      setWithdrawAddress('');
      setWithdrawNetwork('');
      await getWallet();
      if (page !== 1) {
        setPage(1);
      } else {
        await getWalletHistory();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to create withdraw request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sortedTransactions = useMemo(
    () => [...transactions].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [transactions]
  );
  return (
    <section className="wallet-page">
      <header className="wallet-page__header">
        <div>
          <h1>{isDepositMode ? 'Deposit Funds' : 'Withdraw Funds'}</h1>
          <p>
            {isDepositMode
              ? 'Create a fiat deposit request and track its history.'
              : 'Submit a fiat withdrawal request. Balance is checked before sending.'}
          </p>
        </div>
      </header>

      <section className="wallet-grid">
        <Card as="article" className="wallet-card wallet-card--info wallet-balance-card">
          <div className="wallet-card__head">
            <h2>Wallet Balance</h2>
          </div>
          <div className="wallet-footnote wallet-balance-strip">
            <span>{`Available${wallet?.type ? ` (${wallet.type})` : ''}`}</span>
            <strong>{formatCurrency(wallet?.balance)}</strong>
          </div>
          {wallet?.locked_balance !== undefined || wallet?.escrow_balance !== undefined ? (
            <div className="wallet-footnote wallet-balance-strip" style={{ marginTop: 8 }}>

              {wallet?.escrow_balance !== undefined && (
                <>
                  <span style={{ marginLeft: 16 }}>Locked Balance</span>
                  <strong>{formatCurrency(wallet.escrow_balance)}</strong>
                </>
              )}
            </div>
          ) : null}
        </Card>

        <Card as="form" className="wallet-card wallet-card--form wallet-card--deposit-form" onSubmit={isDepositMode ? handleDepositSubmit : handleWithdrawSubmit}>
          <div className="wallet-card__head">
            <h2>{isDepositMode ? 'Deposit Form' : 'Withdraw Form'}</h2>
          </div>

          <div className="wallet-form-grid wallet-form-grid--deposit">
            <label className="wallet-field">
              <span>Amount</span>
              <Input
                className="wallet-input"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                placeholder={isDepositMode ? 'Enter deposit amount' : 'Enter withdraw amount'}
                onChange={(event) => setAmount(event.target.value)}
              />
            </label>

            {!isDepositMode ? (
              <label className="wallet-field wallet-field--wide">
                <span>Withdraw Address</span>
                <Input
                  className="wallet-input"
                  type="text"
                  value={withdrawAddress}
                  placeholder="Enter withdraw address"
                  onChange={(event) => setWithdrawAddress(event.target.value)}
                />
              </label>
            ) : null}

            {!isDepositMode ? (
              <label className="wallet-field wallet-field--wide">
                <span>Network</span>
                <Input
                  className="wallet-input"
                  type="text"
                  value={withdrawNetwork}
                  placeholder="Enter network (e.g. TRC20)"
                  onChange={(event) => setWithdrawNetwork(event.target.value)}
                />
              </label>
            ) : null}

            {isDepositMode ? (
              <label className="wallet-field wallet-field--wide">
                <span>Reference Image</span>
                <Input ref={imageInputRef} className="wallet-upload-hidden" type="file" accept="image/*" onChange={handleImageChange} />
                <div className={`wallet-upload ${imageFile ? 'is-selected' : ''}`}>
                  <button type="button" className="wallet-upload__button" onClick={() => imageInputRef.current?.click()}>
                    {imageFile ? 'Change Image' : 'Choose Image'}
                  </button>
                  <span className="wallet-upload__filename" title={imageFile?.name || ''}>
                    {imageFile ? imageFile.name : 'No file selected'}
                  </span>
                </div>
                {imagePreviewUrl ? (
                  <div className="wallet-upload-preview">
                    <img src={imagePreviewUrl} alt="Reference preview" />
                  </div>
                ) : null}
              </label>
            ) : null}
          </div>

          <div className="wallet-form-actions">
            <Button variant="destructive" type="submit" className="wallet-action-button" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : isDepositMode ? 'Submit Deposit' : 'Submit Withdraw'}
            </Button>
            <Button type="button" variant="outline" className="wallet-outline-button" onClick={resetForm} disabled={isSubmitting}>
              Reset
            </Button>
          </div>
        </Card>
      </section>

      <section className="wallet-history wallet-history--deposit">
        <div className="wallet-history__head">
          <div>
            <h2>{isDepositMode ? 'Deposit History' : 'Withdraw History'}</h2>
          </div>
          <div className="wallet-history__filters" role="group" aria-label="Wallet history filters">
            {HISTORY_STATUS_FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                className={`history-filter ${statusFilter === filter.value ? 'is-active' : ''}`}
                onClick={() => {
                  setStatusFilter(filter.value);
                  setPage(1);
                }}
                disabled={isHistoryLoading}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <div className="history-table-wrap wallet-history__table wallet-history-table-shell">
          <Table className={`history-table history-table--wallet ${isDepositMode ? 'wallet-history-table--deposit' : 'wallet-history-table--withdraw'}`}>
            <TableHeader>
              {isDepositMode ? (
                <>
                  <TableHead>S.No</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Approved Amount</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </>
              ) : (
                <>
                  <TableHead>S.No</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Network</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Date</TableHead>
                </>
              )}
            </TableHeader>
            <TableBody>
              {isHistoryLoading ? (
                <TableRow className="wallet-history-empty-row">
                  <TableCell className="wallet-history-empty-cell">{`Loading ${isDepositMode ? 'deposit' : 'withdraw'} history...`}</TableCell>
                </TableRow>
              ) : sortedTransactions.length === 0 ? (
                <TableRow className="wallet-history-empty-row">
                  <TableCell className="wallet-history-empty-cell">{`No ${isDepositMode ? 'deposit' : 'withdraw'} history yet.`}</TableCell>
                </TableRow>
              ) : (
                sortedTransactions.map((transaction, index) => {
                  const referenceValue = String(transaction?.reference || '-');
                  const hasImageReference = referenceValue !== '-'
                    && (
                      referenceValue.startsWith('data:image/')
                      || referenceValue.startsWith('blob:')
                      || /^https?:\/\//i.test(referenceValue)
                      || referenceValue.startsWith('/')
                      || referenceValue.includes('/images/')
                      || /\.(png|jpe?g|webp|gif|svg|bmp|avif)$/i.test(referenceValue)
                    );
                  const referenceUrl = hasImageReference ? resolveReferenceImageUrl(referenceValue) : '';
                  const approvedAmountValue = Number(transaction?.approvedAmount);
                  const hasApprovedAmount = String(transaction?.approvedAmount || '').trim() !== ''
                    && Number.isFinite(approvedAmountValue);
                  return (
                    <TableRow key={transaction.id}>
                      <TableCell as="strong" className="wallet-deposit-table__sno">
                        {(page - 1) * DEPOSIT_HISTORY_PAGE_SIZE + index + 1}
                      </TableCell>
                      {isDepositMode ? (
                        <>
                          <TableCell className="wallet-deposit-table__amount">{formatCurrency(transaction.amount)}</TableCell>
                          <TableCell className="wallet-deposit-table__approved-amount">
                            {hasApprovedAmount ? formatCurrency(approvedAmountValue) : '-'}
                          </TableCell>
                          <TableCell className="wallet-deposit-table__reference" title={referenceValue}>
                            {referenceUrl ? (
                              <img
                                src={referenceUrl}
                                alt="Deposit reference"
                                className="wallet-deposit-table__reference-image"
                                loading="lazy"
                              />
                            ) : referenceValue}
                          </TableCell>
                          <TableCell className={`history-result-pill ${getStatusClassName(transaction.status)}`}>
                            {transaction.status}
                          </TableCell>
                          <TableCell>{formatDate(transaction.createdAt)}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="wallet-withdraw-table__address" title={String(transaction?.address || '-')}>
                            {String(transaction?.address || '-')}
                          </TableCell>
                          <TableCell className="wallet-withdraw-table__network" title={String(transaction?.network || '-')}>
                            {String(transaction?.network || '-')}
                          </TableCell>
                          <TableCell className="wallet-deposit-table__amount">{formatCurrency(transaction.amount)}</TableCell>
                          <TableCell className={`history-result-pill ${getStatusClassName(transaction.status)}`}>
                            {transaction.status}
                          </TableCell>
                          <TableCell className="wallet-withdraw-table__reason" title={String(transaction?.reason || '-')}>
                            {String(transaction?.reason || '-')}
                          </TableCell>
                          <TableCell>{formatDate(transaction.createdAt)}</TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 ? (
          <div className="wallet-history-pagination" aria-label={`${isDepositMode ? 'Deposit' : 'Withdraw'} history pagination`}>
            <button
              type="button"
              className="wallet-pagination-button wallet-pagination-arrow"
              onClick={() => setPage((previous) => Math.max(previous - 1, 1))}
              disabled={isHistoryLoading || page <= 1}
              aria-label="Previous page"
            >
              {'<'}
            </button>

            {paginationItems.map((item, index) => (item === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} className="wallet-pagination-ellipsis">...</span>
            ) : (
              <button
                key={item}
                type="button"
                className={`wallet-pagination-button wallet-pagination-number ${page === item ? 'is-active' : ''}`}
                onClick={() => setPage(item)}
                disabled={isHistoryLoading || page === item}
                aria-label={`Page ${item}`}
              >
                {item}
              </button>
            )))}

            <button
              type="button"
              className="wallet-pagination-button wallet-pagination-arrow"
              onClick={() => setPage((previous) => Math.min(previous + 1, totalPages))}
              disabled={isHistoryLoading || page >= totalPages}
              aria-label="Next page"
            >
              {'>'}
            </button>
          </div>
        ) : null}
      </section>
    </section>
  );
}
